import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Toggle } from "@/components/Toggle";
import { BottomSheet } from "@/components/BottomSheet";
import { Spinner } from "@/components/Spinner";
import {
  IdCard,
  FileText,
  Truck,
  CreditCard,
} from "lucide-react";
import type { PortalState } from "@/store/portal-store";

interface Step1Props {
  state: PortalState;
  updateConsent: (key: keyof PortalState["consents"], value: boolean) => void;
  onNext: () => void;
}

const requirements = [
  { icon: IdCard, label: "Driver's license" },
  { icon: FileText, label: "USDOT number" },
  { icon: Truck, label: "Truck VIN" },
  { icon: CreditCard, label: "Payment method" },
];

export function Step1Landing({ state, updateConsent, onNext }: Step1Props) {
  const [loading, setLoading] = useState(false);
  const [disclosure, setDisclosure] = useState<string | null>(null);

  if (state.linkExpired) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-gray-400" />
        </div>
        <h1 className="text-xl font-bold mb-2">This link has expired</h1>
        <p className="text-gray-500">
          Please contact your agent for a new quote link.
        </p>
      </div>
    );
  }

  const allConsented = Object.values(state.consents).every(Boolean);

  const handleStart = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onNext();
    }, 1500);
  };

  return (
    <div className="flex flex-col items-center px-6 py-8">
      {/* Logo */}
      <div className="w-[120px] h-[40px] bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg flex items-center justify-center text-white font-bold text-xl mb-6">
        ARTUS
      </div>

      <h1 className="text-2xl font-bold text-center mb-2">
        Get Your Trucking Insurance Quote
      </h1>
      <p className="text-gray-500 text-center mb-6">
        Complete in under 10 minutes. Have these ready:
      </p>

      {/* Requirements checklist */}
      <div className="w-full bg-gray-50 rounded-xl p-4 mb-6">
        {requirements.map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-3 py-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-sm font-medium">{label}</span>
          </div>
        ))}
      </div>

      {/* Consent toggles */}
      <div className="w-full space-y-1 mb-6">
        <Toggle
          checked={state.consents.tcpa}
          onChange={(v) => updateConsent("tcpa", v)}
          label="I consent to receive SMS messages from Artus Insurance"
          linkText="View disclosure"
          onLinkClick={() => setDisclosure("tcpa")}
        />
        <Toggle
          checked={state.consents.esign}
          onChange={(v) => updateConsent("esign", v)}
          label="I agree to use electronic signatures"
          linkText="E-SIGN disclosure"
          onLinkClick={() => setDisclosure("esign")}
        />
        <Toggle
          checked={state.consents.privacy}
          onChange={(v) => updateConsent("privacy", v)}
          label="I have read and accept the Privacy Policy"
          linkText="Privacy Policy"
          onLinkClick={() => setDisclosure("privacy")}
        />
        <Toggle
          checked={state.consents.cameraLocation}
          onChange={(v) => updateConsent("cameraLocation", v)}
          label="I allow access to my camera and location for verification"
        />
      </div>

      {/* CTA */}
      <Button
        className="w-full h-12 text-base"
        disabled={!allConsented || loading}
        onClick={handleStart}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Spinner size="sm" className="border-white border-t-transparent" />
            Starting...
          </span>
        ) : (
          "Let's Get Started"
        )}
      </Button>

      <p className="text-xs text-gray-400 text-center mt-6">
        Artus Insurance Inc. — An AgentsHouse MGU Company
      </p>

      {/* Disclosure bottom sheet */}
      <BottomSheet
        open={disclosure !== null}
        onClose={() => setDisclosure(null)}
        title={
          disclosure === "tcpa"
            ? "TCPA/SMS Disclosure"
            : disclosure === "esign"
            ? "E-SIGN Act Disclosure"
            : "Privacy Policy"
        }
      >
        <p className="text-sm text-gray-600 leading-relaxed">
          {disclosure === "tcpa" &&
            "By consenting, you agree to receive automated SMS messages from Artus Insurance related to your insurance quote and policy. Message and data rates may apply. You can opt out at any time by replying STOP."}
          {disclosure === "esign" &&
            "By agreeing, you consent to conduct business electronically and to use electronic signatures in lieu of handwritten signatures, as permitted by the Electronic Signatures in Global and National Commerce Act (E-SIGN Act)."}
          {disclosure === "privacy" &&
            "We collect and use your personal information to provide insurance services. Your data is protected and will not be sold to third parties. For full details, visit our privacy policy page."}
        </p>
        <Button className="w-full mt-4" onClick={() => setDisclosure(null)}>
          Close
        </Button>
      </BottomSheet>
    </div>
  );
}
